import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { app as appApi } from "./api";

/**
 * Contexto global da lista de conversas + cache de conteúdo por taskId.
 *
 * Motivação: antes, cada página (ChatPage e SettingsPage) carregava a
 * lista separadamente, com state local. Resultado:
 *   - ao abrir Configurações, o sidebar ficava 200–1500 ms mostrando
 *     "Sem conversas" antes do fetch terminar;
 *   - ao clicar num chat vindo de Configurações, o chat também aparecia
 *     vazio até o status() chegar.
 *
 * Aqui mantemos:
 *   - `tasksRaw` em estado React (compartilhado entre páginas)
 *   - hidratação síncrona de sessionStorage no primeiro render (render
 *     instantâneo a partir do cache da última visita)
 *   - cache por taskId (`taskContentCache`) acessível por qualquer
 *     componente, sobrevive a montagens/desmontagens da ChatPage
 */

const SESSION_KEY = "linkia_tasks_cache_v1";
const CONTENT_KEY = "linkia_task_content_cache_v1";

// Cache de conteúdo por taskId em escopo de módulo. Sobrevive a
// montagens/desmontagens dos componentes. Hidratado de sessionStorage
// na primeira leitura.
let _contentCacheInited = false;
const contentCache = new Map(); // taskId -> { messages, timeline, durationMs }

function hydrateContentCache() {
    if (_contentCacheInited) return;
    _contentCacheInited = true;
    try {
        const raw = sessionStorage.getItem(CONTENT_KEY);
        if (raw) {
            const obj = JSON.parse(raw);
            for (const [k, v] of Object.entries(obj || {})) contentCache.set(k, v);
        }
    } catch { /* silencioso */ }
}

function persistContentCache() {
    try {
        // Limita a 50 conversas no sessionStorage pra não passar do limite
        const entries = Array.from(contentCache.entries()).slice(-50);
        const obj = Object.fromEntries(entries);
        sessionStorage.setItem(CONTENT_KEY, JSON.stringify(obj));
    } catch { /* silencioso (quota cheia, etc.) */ }
}

// API pública para os componentes manipularem o cache de conteúdo
export const taskContentCache = {
    get(taskId) {
        hydrateContentCache();
        return contentCache.get(taskId);
    },
    set(taskId, content) {
        hydrateContentCache();
        contentCache.set(taskId, content);
        // Debounce simples: agenda persistência no próximo microtask
        scheduleContentPersist();
    },
    delete(taskId) {
        hydrateContentCache();
        contentCache.delete(taskId);
        scheduleContentPersist();
    },
    clear() {
        contentCache.clear();
        try { sessionStorage.removeItem(CONTENT_KEY); } catch { /* noop */ }
    },
};

let _persistScheduled = false;
function scheduleContentPersist() {
    if (_persistScheduled) return;
    _persistScheduled = true;
    queueMicrotask(() => {
        _persistScheduled = false;
        persistContentCache();
    });
}

// ----------------------------------------------------------------
// Provider da lista de tarefas
// ----------------------------------------------------------------

const TasksContext = createContext(null);

function readSessionTasks() {
    try {
        const raw = sessionStorage.getItem(SESSION_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return null;
        return parsed;
    } catch {
        return null;
    }
}

function writeSessionTasks(list) {
    try {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(list || []));
    } catch { /* noop (quota cheia ou modo privado) */ }
}

export function TasksProvider({ children }) {
    // Hidratação síncrona — render zero já tem os dados da sessão anterior.
    const initial = useMemo(() => readSessionTasks(), []);
    const hasCache = Array.isArray(initial) && initial.length > 0;

    const [tasksRaw, setTasksRaw] = useState(initial || []);
    // "loading" só é verdadeiro quando ainda não temos NADA pra mostrar.
    // Se hidratamos do cache, vamos direto para "loading: false" — assim
    // o sidebar não pisca skeleton em cima de dados que ele já tem.
    const [loading, setLoading] = useState(!hasCache);
    const inFlightRef = useRef(false);

    const loadTasks = useCallback(async () => {
        if (inFlightRef.current) return;
        // Sem token = não tem o que carregar (usuário deslogado).
        if (typeof window !== "undefined" && !localStorage.getItem("linkia_token")) {
            setLoading(false);
            return;
        }
        inFlightRef.current = true;
        try {
            const list = await appApi.listTasks(100);
            const arr = Array.isArray(list) ? list : [];
            setTasksRaw(arr);
            writeSessionTasks(arr);
        } catch {
            // silencioso — mantém o cache exibido
        } finally {
            setLoading(false);
            inFlightRef.current = false;
        }
    }, []);

    // Primeiro mount: sempre tenta refrescar em background.
    useEffect(() => {
        loadTasks();
        // Recarrega tarefas quando o usuário faz login (token aparece).
        // Detecta via evento 'storage' (em outras abas) e via listener custom.
        const onAuth = () => loadTasks();
        window.addEventListener("linkia-auth-changed", onAuth);
        return () => window.removeEventListener("linkia-auth-changed", onAuth);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Permite que componentes injetem alterações otimistas (ex.: adicionar
    // título de uma conversa nova) sem refetch.
    const upsertTask = useCallback((task) => {
        if (!task?.id) return;
        setTasksRaw((prev) => {
            const idx = prev.findIndex((x) => x.id === task.id);
            let next;
            if (idx >= 0) {
                next = prev.slice();
                next[idx] = { ...prev[idx], ...task };
            } else {
                next = [task, ...prev];
            }
            writeSessionTasks(next);
            return next;
        });
    }, []);

    const removeTask = useCallback((id) => {
        setTasksRaw((prev) => {
            const next = prev.filter((x) => x.id !== id);
            writeSessionTasks(next);
            return next;
        });
        taskContentCache.delete(id);
    }, []);

    const value = useMemo(
        () => ({ tasksRaw, loading, loadTasks, upsertTask, removeTask }),
        [tasksRaw, loading, loadTasks, upsertTask, removeTask]
    );

    return <TasksContext.Provider value={value}>{children}</TasksContext.Provider>;
}

export function useTasks() {
    const ctx = useContext(TasksContext);
    if (!ctx) throw new Error("useTasks precisa estar dentro de <TasksProvider>");
    return ctx;
}
